import { styled } from '@/stitches.config';
import { forwardRef } from 'react';

import { Variants } from '@common/types';
import { IconButtonProps } from './IconButton.types';

const StyledIconButton = styled('button', {
  // primary button colors
  $$button: '$colors$darkBlue200',
  $$buttonLight: '$colors$darkBlue300',
  $$buttonDark: '$colors$darkBlue100',
  $$buttonDisabled: '$colors$coolGray100',

  // secondary button colors
  $$buttonSecondary: '$colors$darkGray100',
  $$buttonSecondaryHover: '$colors$darkBlue800',
  $$buttonSecondaryActive: '$colors$darkBlue700',

  // Reset
  all: 'unset',

  // Custom styles
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
  width: '$8',
  borderRadius: '$20',

  '> *': {
    height: '$6',
    width: '$6',
  },

  '&:disabled': {
    background: '$$buttonDisabled',
    color: '$darkGray200',
    pointerEvents: 'none',
  },

  '&:hover': {
    cursor: 'pointer',
  },

  // Variants
  variants: {
    variant: {
      primary: {
        backgroundColor: '$$buttonLight',
        color: '$textAccent',
        '&:hover': {
          backgroundColor: '$$button',
        },
        '&:active': {
          backgroundColor: '$$buttonDark',
        },
        '&:focus': {
          border: '1px solid $colors$darkBlue200',
        },
      },
      secondary: {
        color: '$$buttonSecondary',
        '&:hover': {
          backgroundColor: '$$buttonSecondaryHover',
        },
        '&:active': {
          backgroundColor: '$$buttonSecondaryActive',
        },
        '&:disabled': {
          backgroundColor: '$white',
          color: '$$buttonDisabled',
        },
        '&:focus': {
          border: '1px solid $colors$darkBlue200',
        },
      },
      tertiary: {},
    },
  },
  defaultVariants: {
    variant: Variants.PRIMARY,
  },
});

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  const { children } = props;
  return (
    <StyledIconButton role="button" ref={ref} {...props}>
      {children}
    </StyledIconButton>
  );
});

IconButton.displayName = 'IconButton';

export default IconButton;
